import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import './pillTabBar_26.css';

/**
 * pillTabBar_26 — pill-style segmented tab bar (Hardware/Software, Document Center, etc.)
 *
 * @param {Array<{ value: string|number, label: string, count?: number, iconActive?: string, iconInactive?: string, iconNode?: React.ReactNode }>} tabs
 *   iconNode renders a React element (e.g. an MUI icon) before the label; it inherits the tab's color.
 * @param {string|number} value - active tab value
 * @param {(value: string|number) => void} onChange
 * @param {string} [className] - extra class on root (e.g. pillTabBar_26-compact)
 * @param {boolean} [showZero=true] - show count badge when count is 0
 * @param {Array<string|number>} [pinnedValues] - values currently pinned; enables pin button on hover
 * @param {(value: string|number) => void} [onPinChange] - called when pin button is clicked; hidden by default
 * @param {React.ReactNode} [renderPrefix] - optional custom content rendered before the tabs
 * @param {(tab: object, isActive: boolean) => React.ReactNode} [renderSuffix] - optional custom content rendered after the label/count
 * @param {boolean} [scrollArrows=false] - show left/right scroll buttons when the row overflows (pair with pillTabBar_26-scrollable)
 */
const PillTabBar_26 = ({
  tabs,
  value,
  onChange,
  className = '',
  showZero = true,
  pinnedValues = null,
  onPinChange = null,
  renderPrefix = null,
  renderSuffix = null,
  scrollArrows = false,
}) => {
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 1);
    setCanRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
  }, []);

  // Track scroll position + viewport resize so the arrows show/hide correctly.
  useEffect(() => {
    if (!scrollArrows) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;
    const handler = () => updateArrows();
    el.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler);
    updateArrows();
    return () => {
      el.removeEventListener('scroll', handler);
      window.removeEventListener('resize', handler);
    };
  }, [scrollArrows, updateArrows]);

  // Re-measure after every render so async tab/count changes update arrow visibility.
  useLayoutEffect(() => {
    if (scrollArrows) updateArrows();
  });

  const scrollByDir = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  const track = (
    <Box className="pillTabBar_26-track" ref={trackRef}>
      {renderPrefix}
      {tabs.map((tab) => {
        const isActive = value === tab.value;
        const hasIcon = tab.iconActive || tab.iconInactive;
        const showCount = tab.count != null && (showZero || tab.count > 0);
        const isPinned = Array.isArray(pinnedValues) && pinnedValues.includes(tab.value);

        return (
          <Box
            key={tab.value}
            component="button"
            type="button"
            onClick={() => onChange(tab.value)}
            className={`pillTabBar_26-tab${isActive ? ' active' : ''}`}
            aria-pressed={isActive}
          >
            <Box className="pillTabBar_26-label">
              {hasIcon && (
                <img
                  src={isActive ? tab.iconActive : tab.iconInactive}
                  alt=""
                  className="pillTabBar_26-icon"
                />
              )}
              {tab.iconNode && (
                <span className="pillTabBar_26-iconNode">{tab.iconNode}</span>
              )}
              <span className={isActive ? 'labelActive' : 'labelInactive'}>{tab.label}</span>
              {showCount && (
                <span className={
                  isActive
                    ? 'pillTabBar_26-count pillTabBar_26-count-active'
                    : tab.count > 0
                      ? 'pillTabBar_26-count pillTabBar_26-count-has-data'
                      : 'pillTabBar_26-count pillTabBar_26-count-inactive'
                }>
                  {tab.count}
                </span>
              )}
              {onPinChange && (
                <span
                  role="button"
                  className={`pillTabBar_26-pin-btn${isPinned ? ' pinned' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onPinChange(tab.value); }}
                  title={isPinned ? 'Unpin' : 'Pin'}
                >
                  {isPinned
                    ? <PushPinIcon sx={{ fontSize: 15 }} />
                    : <PushPinOutlinedIcon sx={{ fontSize: 15 }} />
                  }
                </span>
              )}
              {renderSuffix && renderSuffix(tab, isActive)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box className={`pillTabBar_26 ${className}`.trim()}>
      {scrollArrows ? (
        <Box className="pillTabBar_26-scroll-wrap">
          {track}
          {canLeft && (
            <button
              type="button"
              aria-label="Scroll tabs left"
              className="pillTabBar_26-scroll-arrow left"
              onClick={() => scrollByDir(-1)}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </button>
          )}
          {canRight && (
            <button
              type="button"
              aria-label="Scroll tabs right"
              className="pillTabBar_26-scroll-arrow right"
              onClick={() => scrollByDir(1)}
            >
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </button>
          )}
        </Box>
      ) : track}
    </Box>
  );
};

export default PillTabBar_26;
